import React, { useRef, useState } from "react";
import { apiUpload } from "~/lib/api";

type Mode = "paste" | "upload";
type UploadState = "idle" | "uploading" | "done" | "error";

interface ResumeInputProps {
  value: string;
  onChange: (text: string) => void;
  disabled: boolean;
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none";

const ResumeInput: React.FC<ResumeInputProps> = ({ value, onChange, disabled }) => {
  const [mode, setMode] = useState<Mode>("upload");
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [wordCount, setWordCount] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleModeSwitch = (next: Mode) => {
    setMode(next);
    if (next === "paste") {
      setUploadState("idle");
      setUploadError(null);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadState("uploading");
    setUploadError(null);

    try {
      const { text } = await apiUpload<{ text: string }>("/api/v1/trial/extract", file);
      onChange(text);
      setWordCount(text.trim().split(/\s+/).length);
      setUploadState("done");
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed.");
      setUploadState("error");
      onChange("");
    }

    e.target.value = "";
  };

  const handleClear = () => {
    onChange("");
    setUploadState("idle");
    setUploadError(null);
    setWordCount(0);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Your resume</label>
        <div className="flex rounded-md border border-input overflow-hidden text-xs">
          {(["upload", "paste"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => handleModeSwitch(m)}
              disabled={disabled}
              className={`px-3 py-1 transition-colors ${
                mode === m
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted"
              }`}
            >
              {m === "upload" ? "Upload PDF" : "Paste text"}
            </button>
          ))}
        </div>
      </div>

      {mode === "paste" && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste your resume text here..."
          rows={8}
          required
          className={inputCls}
          disabled={disabled}
        />
      )}

      {mode === "upload" && uploadState === "idle" && (
        <label className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input bg-background px-4 py-10 text-sm text-muted-foreground transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary hover:text-foreground"}`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <span>Click to upload your resume <span className="font-medium text-foreground">PDF</span></span>
          <span className="text-xs">Max 5MB</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="sr-only"
            disabled={disabled}
            onChange={handleFileChange}
          />
        </label>
      )}

      {mode === "upload" && uploadState === "uploading" && (
        <div className="flex items-center gap-3 rounded-md border border-input bg-background px-4 py-10 justify-center">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-sm text-muted-foreground">Extracting text from PDF...</span>
        </div>
      )}

      {mode === "upload" && uploadState === "error" && (
        <div className="space-y-2">
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-destructive">{uploadError}</p>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-destructive underline underline-offset-2 ml-4 hover:no-underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {mode === "upload" && uploadState === "done" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-green-800 font-medium">Resume extracted — {wordCount.toLocaleString()} words</span>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-green-700 underline underline-offset-2 hover:no-underline"
            >
              Clear
            </button>
          </div>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={6}
            className={`${inputCls} text-muted-foreground`}
            disabled={disabled}
            aria-label="Extracted resume text — review and edit if needed"
          />
          <p className="text-xs text-muted-foreground">Review the extracted text above. Edit if anything looks off.</p>
        </div>
      )}
    </div>
  );
};

export default ResumeInput;
